/**
 * `useEffectiveReviewPolicy` (M3) — resolves the effective review policy
 * for an assignment (assignment override → org defaults → fallback) local-first,
 * mirroring the write-path's `effective_review_policy`. It doesn't know about the
 * final project's single grading (a column from another vertical); the server applies it and
 * is the final decision-maker — this only gates the UI.
 */
import { type Accessor, createMemo } from "solid-js";
import { resolvePolicy, type ReviewPolicy } from "@space/shared";
import { useReviewPolicy, useTutoringSettings } from "./hooks";

export function useEffectiveReviewPolicy(
  assignmentId: Accessor<string | undefined>,
  orgId: Accessor<string | undefined>,
): () => ReviewPolicy {
  const settings = useTutoringSettings(orgId);
  const override = useReviewPolicy(assignmentId);
  const policy = createMemo<ReviewPolicy>(() => {
    const s = (settings.data ?? [])[0];
    const o = (override.data ?? [])[0];
    return resolvePolicy(
      s
        ? {
            allowResubmission: (s.allow_resubmission ?? 1) !== 0,
            maxVersions: s.max_versions ?? 3,
            slaDays: s.sla_days ?? 3,
          }
        : null,
      o
        ? {
            allowResubmission: o.allow_resubmission == null ? null : o.allow_resubmission !== 0,
            maxVersions: o.max_versions ?? null,
            slaDays: o.sla_days ?? null,
          }
        : {},
    );
  });
  return policy;
}
